import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import useLockedBody from '@/hooks/useLockedBody';
import useKeyboardHeight from '@/hooks/useKeyboardHeight';
import { useEffect, useRef, useState } from 'react';
import useResponsive from '@/hooks/useResponsive';

export default function ModalContainer({
  children,
  open = true,
  interactionOutside = true,
  onClose,
  className,
  portalBackground = 'bg-black',
  background = 'bg-black',
  border = 'border border-white',
  text = 'text-white',
}: {
  children: React.ReactNode;
  open?: boolean;
  interactionOutside?: boolean;
  className?: string;
  portalBackground?: string;
  background?: string;
  border?: string;
  text?: string;
  onClose?: () => void;
}) {
  const [scrollLocked, setScrollLocked] = useState(false);
  const [, setLocked] = useLockedBody(false, (lockState) => setScrollLocked(lockState));
  const divRef = useRef<HTMLDivElement>(null);
  const { keyboardHeight } = useKeyboardHeight();
  const { isMobile } = useResponsive();

  useEffect(() => {
    // Modals lock the body when presented.
    setLocked(open);
  }, [open, setLocked]);

  // Once the body is locked, we need to scroll the modal to the center of the screen.
  // Note: It's important that the body is locked, otherwise the view will have shifted when you close the modal.
  useEffect(() => {
    if (divRef.current && isMobile && scrollLocked) {
      if (window.visualViewport) {
        const rect = divRef.current.getBoundingClientRect();
        const targetScroll = rect.top + 0.5 * rect.height - window.visualViewport.height * 0.5;

        window.scrollTo({ top: targetScroll });
      }
    }
  }, [scrollLocked]);

  // Every time the keyboard is opened, shift the view. This ensures the modal stays centred, while also allowing
  // the user to scroll the modal.
  const lastKeyboardHeight = useRef(0);
  useEffect(() => {
    if (scrollLocked) {
      window.scrollBy(0, 0.5 * (keyboardHeight - lastKeyboardHeight.current));

      lastKeyboardHeight.current = keyboardHeight;
    }
  }, [keyboardHeight, scrollLocked]);

  return (
    <Dialog.Root
      open={open}
      modal={!interactionOutside}
      onOpenChange={(state) => !state && onClose && onClose()}
    >
      <AnimatePresence mode="wait">
        <Dialog.Portal forceMount>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={clsx([
              'fixed inset-0 z-[100]',
              portalBackground,
              isMobile ? 'bg-opacity-[0.925]' : 'bg-opacity-[0.85]'

            ])}
          />
          <div className="fixed inset-0 h-full w-full pointer-events-none" /> {/* Click blocker */}
          <Dialog.Content forceMount onOpenAutoFocus={(ev) => ev.preventDefault()}>
            <motion.div
              ref={divRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={clsx([
                'fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] overflow-hidden',
                'outline-none z-[100] max-h-[100vh] max-w-[min(100vw,2400px)]',
                background,
                border,
                text,
                className,
              ])}
            >
              {children}
            </motion.div>
          </Dialog.Content>
        </Dialog.Portal>
      </AnimatePresence>
    </Dialog.Root>
  );
}
